import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { storeSession } from "../authClient";

/**
 * Landing page for the Google OAuth redirect.
 *
 * The backend no longer puts the JWT in the URL — it sends a single-use,
 * 60-second code which we trade for the real token over a POST. See
 * backend-/utils/authExchange.js for why.
 */
export default function AuthSuccess() {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");

    if (!code) {
      // Previously this branch did nothing at all, leaving the user parked on
      // "Logging in with Google..." with no way forward.
      setError("Missing sign-in code. Please try signing in again.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await axios.post("/api/auth/exchange", { code });
        const token = res.data?.token;
        if (!token) throw new Error("No token in exchange response");
        if (cancelled) return;

        // Carries refreshToken too now — /api/auth/exchange returns the whole
        // session object, not a bare JWT.
        storeSession(res.data);
        try {
          const decoded = jwtDecode(token);
          const userId = decoded.id || decoded._id || decoded.userId;
          if (userId) localStorage.setItem("userId", userId);
        } catch (e) {
          console.error("Token decode error:", e);
        }

        // Strip the (now-spent) code from the address bar so it isn't left in
        // history or sent as a Referer by anything the dashboard loads.
        window.history.replaceState({}, "", "/auth/success");
        navigate("/home", { replace: true });
      } catch (e) {
        if (cancelled) return;
        console.error("Auth exchange failed:", e);
        setError("Sign-in link expired or already used. Please try again.");
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <p>{error}</p>
        <button onClick={() => navigate("/login", { replace: true })}>
          Back to sign in
        </button>
      </div>
    );
  }

  return <p>Logging in with Google...</p>;
}
